/**
 * @hector/shared-types/memory — Zod schemas for user memory management.
 *
 * Contrats partagés pour la mémoire utilisateur (feature #271) consommée par :
 *   - apps/backend     : repository + routes PATCH/GET endpoints
 *   - apps/legalworkspace : prompts injection, UI édition des blocs
 *
 * Schémas de mémoire persistée en OVH (table `user_memory_blocks`).
 */

import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// Catalogue des labels et limites
// ────────────────────────────────────────────────────────────────────────────

/**
 * Enum des labels de mémoire supportés. Extensible pour futures phases.
 * Les limites de caractères correspondantes sont dans MEMORY_CHAR_LIMITS.
 */
export const MEMORY_LABELS = ['user_profile', 'user_memory', 'redaction_style'] as const;

/**
 * Type TypeScript correspondant au catalogue MEMORY_LABELS.
 */
export type MemoryLabel = (typeof MEMORY_LABELS)[number];

/**
 * Plafonds en caractères par label. Strict à l'écriture (UpdateMemoryBlockSchema,
 * UserMemoryRepository) et seule source du plafond renvoyé à la lecture : la colonne
 * `user_memory_blocks.char_limit` n'est qu'une copie, jamais relue.
 *
 * @remarks
 * `user_memory` est passé de 2 500 à 10 000 le 2026-09-17 (#1573, amendement de la
 * spec #1572) : les avocats collaient des consignes de travail plus longues que le
 * bloc, et l'agent les condensait après plusieurs refus au plafond. Profil et style
 * sont inchangés.
 */
export const MEMORY_CHAR_LIMITS: Record<MemoryLabel, number> = {
  user_profile: 1500,
  user_memory: 10000,
  redaction_style: 20000,
};

/**
 * Nom français de chaque bloc, tel que l'avocat le lit dans l'écran Mémoire.
 * Utilisé par les messages de refus pour nommer le bloc sans jargon technique.
 */
export const MEMORY_LABEL_NAMES: Record<MemoryLabel, string> = {
  user_profile: 'Profil',
  user_memory: 'Habitudes de travail',
  redaction_style: 'Style de rédaction',
};

/**
 * Plafond en caractères du digest de mémoire-dossier injecté en session.
 *
 * @remarks
 * Le digest (`case_memory.digest`) résume l'avancement et les décisions prises sur le dossier.
 * Mesuré en production le 2026-10-12 : un digest pèse environ 3 caractères par token (pas 4),
 * donc 20 000 caractères ≈ 6 700 tokens. Tout budget de sortie LLM qui réécrit le digest se
 * dimensionne sur 3 caractères par token, sinon la sortie est tronquée avant le plafond.
 * Distinct des `MEMORY_CHAR_LIMITS` (mémoire utilisateur) — les deux coexistent.
 */
export const CASE_DIGEST_CHAR_LIMIT = 20000;

/** Bloc dont une écriture peut être refusée pour dépassement de plafond. */
export type MemoryCapLabel = MemoryLabel | 'case_memory';

/** Nom français et plafond d'un bloc plafonné, mémoire du dossier comprise. */
function memoryCapOf(label: MemoryCapLabel): { name: string; limit: number } {
  return label === 'case_memory'
    ? { name: 'Mémoire du dossier', limit: CASE_DIGEST_CHAR_LIMIT }
    : { name: MEMORY_LABEL_NAMES[label], limit: MEMORY_CHAR_LIMITS[label] };
}

/**
 * Précisions facultatives du message de refus au plafond.
 */
export interface MemoryOverCapMessageOptions {
  /** Taille du bloc avant l'opération ; ajoutée au diagnostic quand elle est fournie. */
  currentLength?: number;
  /**
   * `agent` : la conduite à tenir est écrite pour l'agent qui tient le bloc en autonomie
   * (relire, puis remplacer ou supprimer avant d'ajouter). Absent : conduite écrite pour
   * l'avocat, qui lit ce message dans l'écran Mémoire.
   */
  reader?: 'agent';
}

/**
 * Message de refus d'une écriture qui dépasserait le plafond d'un bloc mémoire.
 *
 * Partagé par le schéma de l'écran Mémoire, les dépôts et l'outil `update_memory` :
 * l'avocat et l'agent lisent le même diagnostic chiffré, qui dit de combien réduire
 * au lieu d'un simple « trop long ».
 *
 * La place restante vaut `plafond − (résultat − texte demandé)` : le plafond entier
 * pour un remplacement, et ce qui reste après l'existant (saut de ligne compris)
 * pour un ajout. Elle est bornée à 0 si l'existant occupe déjà tout le bloc.
 *
 * WHY une conduite propre à l'agent (`reader: 'agent'`) : le 2026-10-12, `update_memory`
 * a été refusé 22 fois en une journée sur des mémoires de dossier pleines, avec un message
 * qui ne disait pas quoi faire. L'instantané de session peut être périmé et `delete` exige
 * la ligne exacte : sans relecture, l'agent retente le même ajout ou supprime une ligne
 * qui n'existe plus. La conduite nomme `delete` et met en garde contre `replace` : dire
 * « remplace les lignes périmées » invitait à appeler `replace` avec une seule ligne, or
 * `replace` réécrit tout le bloc — un digest de 19 700 caractères réduit à une ligne.
 *
 * @param label - Bloc refusé
 * @param requestedLength - Longueur du texte soumis (le contenu ajouté pour un `append`)
 * @param resultingLength - Longueur qu'aurait le bloc après l'opération
 * @param options - Taille actuelle du bloc et lecteur visé
 * @returns Message en français, avec taille demandée, plafond, place restante et dépassement
 */
export function formatMemoryOverCapMessage(
  label: MemoryCapLabel,
  requestedLength: number,
  resultingLength: number,
  options: MemoryOverCapMessageOptions = {},
): string {
  const { name, limit } = memoryCapOf(label);
  const remaining = Math.max(0, limit - (resultingLength - requestedLength));
  const overflow = resultingLength - limit;
  const current = options.currentLength === undefined ? '' : `taille actuelle ${options.currentLength}, `;
  const conduct = options.reader === 'agent'
    ? "Rien n'a été enregistré : relis l'état actuel avec read_memory (l'instantané de session peut être périmé), " +
      "retire par delete, une ligne exacte à la fois, les lignes dépassées par une décision plus récente, puis refais ton ajout. " +
      "replace réécrit le bloc entier : ne l'emploie qu'en reprenant tout ce qui reste valable."
    : "Rien n'a été enregistré : raccourcissez le texte ou libérez de la place dans ce bloc.";
  return (
    `${name} (${label}) : texte de ${requestedLength} caractères refusé — ` +
    `${current}plafond ${limit}, place restante ${remaining}, dépassement ${overflow}. ` +
    conduct
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Énums secondaires
// ────────────────────────────────────────────────────────────────────────────

/**
 * État du bloc : actif ou temporairement mis en pause (mais pas supprimé).
 */
export const MEMORY_BLOCK_STATUSES = ['active', 'paused'] as const;

/**
 * Origine de la dernière mise à jour du bloc.
 *
 * - `seed`   : valeur initiale insérée à la création du compte
 * - `user`   : édition manuelle du bloc
 * - `agent`  : outil mémoire pendant une conversation
 * - `dream`  : consolidation nocturne, ajout ou correction ciblée des habitudes
 * - `review` : leçons utiles issues des corrections et insatisfactions de l'avocat
 * Les écritures automatiques passent par AutomaticHabitRepository, jamais par un
 * remplacement intégral du bloc via les outils génériques.
 */
export const MEMORY_BLOCK_SOURCES = ['seed', 'user', 'agent', 'dream', 'review'] as const;

/**
 * Type TypeScript correspondant au catalogue MEMORY_BLOCK_SOURCES.
 */
export type MemoryBlockSource = (typeof MEMORY_BLOCK_SOURCES)[number];

/**
 * Nombre de tours de conversation entre deux relectures automatiques (#271, #1574).
 *
 * @remarks
 * Un « tour » = un échange avocat → Hector terminé normalement. La relecture part quand
 * le compteur `casediscovery_conversations.memory_review_turn_counter`, incrémenté modulo
 * cet intervalle, retombe à 0. Huit tours laissent à l'avocat le temps de corriger Hector
 * avant la relecture, sans attendre la fin d'une longue conversation (la relecture de fin,
 * 10 minutes après le dernier échange, couvre les conversations courtes).
 */
export const MEMORY_REVIEW_TURN_INTERVAL = 8;

// ────────────────────────────────────────────────────────────────────────────
// Schémas Zod
// ────────────────────────────────────────────────────────────────────────────

/**
 * Schéma complet d'un bloc de mémoire en DB (ou GET response).
 * Tous les champs présents, avec valeurs par défaut applicables.
 */
export const MemoryBlockSchema = z.object({
  /** Label du bloc (clé du catalogue MEMORY_LABELS) */
  label: z.enum(MEMORY_LABELS),

  /** Contenu textuel du bloc */
  value: z.string(),

  /** Limite de caractères appliquée (copie de MEMORY_CHAR_LIMITS[label]) */
  char_limit: z.number().int().positive(),

  /** Versioning simple : incrémenté à chaque mutation */
  version: z.number().int().positive().default(1),

  /** État du bloc : actif ou paused */
  status: z.enum(MEMORY_BLOCK_STATUSES).default('active'),

  /** Qui a modifié ce bloc pour la dernière fois ? */
  updated_by: z.enum(MEMORY_BLOCK_SOURCES).default('seed'),

  /** Timestamp ISO 8601 de la dernière mutation (optionnel au schéma) */
  updated_at: z.string().optional(),
});

/**
 * Type TypeScript inféré du MemoryBlockSchema.
 */
export type MemoryBlock = z.infer<typeof MemoryBlockSchema>;

/**
 * Schéma pour les PATCH requests (UI — mise à jour d'un bloc).
 * Payload client allégée : seuls label, value, status optionnel.
 * Le refine() applique la limite de caractères avec le message de
 * {@link formatMemoryOverCapMessage}, que la route renvoie tel quel à l'écran.
 */
export const UpdateMemoryBlockSchema = z
  .object({
    /** Label obligatoire (discriminant pour la limite) */
    label: z.enum(MEMORY_LABELS),

    /** Nouveau contenu textuel */
    value: z.string(),

    /** Version lue à l'ouverture de l'éditeur : refuse d'écraser un ajout intervenu depuis. */
    expectedVersion: z.number().int().positive().optional(),

    /** Changement d'état optionnel (active/paused) */
    status: z.enum(MEMORY_BLOCK_STATUSES).optional(),
  })
  .superRefine((data, ctx) => {
    const charLength = data.value.length;
    if (charLength > MEMORY_CHAR_LIMITS[data.label]) {
      // L'écran Mémoire remplace le bloc entier : taille demandée = taille finale.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatMemoryOverCapMessage(data.label, charLength, charLength),
        path: ['value'],
      });
    }
  });

/**
 * Type TypeScript inféré du UpdateMemoryBlockSchema.
 */
export type UpdateMemoryBlock = z.infer<typeof UpdateMemoryBlockSchema>;

/**
 * Réponse de `GET /api/memory/config` — tous les blocs du cabinet.
 */
export const MemoryConfigResponseSchema = z.object({
  blocks: z.array(MemoryBlockSchema),
});

/**
 * Type TypeScript inféré du MemoryConfigResponseSchema.
 */
export type MemoryConfigResponse = z.infer<typeof MemoryConfigResponseSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Slice 2 — Tool contracts : read_memory / update_memory
// ────────────────────────────────────────────────────────────────────────────

/**
 * Cibles accessibles aux tools agent pour la lecture/écriture mémoire.
 *
 * - `user_profile` / `user_memory` : blocs mémoire utilisateur, plafonnés par `MEMORY_CHAR_LIMITS`.
 * - `case_memory` : digest de mémoire-dossier, plafonné par `CASE_DIGEST_CHAR_LIMIT` (20 000 caractères).
 *   Le routage vers le repo `case_memory` est effectué en T15 (backend).
 *
 * @remarks
 * Étendu EN PLACE — ne jamais créer de `_V2` parallèle.
 * Tout ajout = modifier ce tableau + régénérer `MemoryWriteTargetSchema` + migrer en DB.
 */
export const MEMORY_WRITE_TARGETS = ['user_profile', 'user_memory', 'case_memory'] as const;

/**
 * Type TypeScript correspondant au catalogue MEMORY_WRITE_TARGETS.
 */
export type MemoryWriteTarget = (typeof MEMORY_WRITE_TARGETS)[number];

/**
 * Schéma Zod enum dérivé du catalogue MEMORY_WRITE_TARGETS.
 * Utilisé comme discriminant dans les schémas d'args des tools mémoire.
 */
export const MemoryWriteTargetSchema = z.enum(MEMORY_WRITE_TARGETS);

/**
 * Opérations supportées par le tool `update_memory`.
 *
 * - `replace` : remplace intégralement le contenu du bloc (content requis)
 * - `append`  : concatène content au bloc existant (content requis)
 * - `delete`  : retire un extrait précis du bloc (content requis — sert de sélecteur)
 * - `clear`   : vide entièrement le bloc (content interdit — l'op est destructive par nature)
 */
export const MemoryWriteOpSchema = z.enum(['replace', 'append', 'delete', 'clear']);

/**
 * Type TypeScript inféré de MemoryWriteOpSchema.
 */
export type MemoryWriteOp = z.infer<typeof MemoryWriteOpSchema>;

/**
 * Arguments du tool agent `read_memory`.
 *
 * `targets` omis = lit tous les blocs disponibles.
 * `targets` fourni = lit uniquement les blocs listés (optimisation tokens).
 */
export const ReadMemoryArgsSchema = z.object({
  /** Sous-ensemble de blocs à lire. Absent = tous les blocs. */
  targets: z.array(MemoryWriteTargetSchema).optional(),
});

/**
 * Type TypeScript inféré de ReadMemoryArgsSchema.
 */
export type ReadMemoryArgs = z.infer<typeof ReadMemoryArgsSchema>;

/**
 * Arguments du tool agent `update_memory`.
 *
 * Invariants validés par superRefine :
 * - `replace | append | delete` exigent `content` non-vide (après trim).
 * - `clear` interdit `content` non-vide (op destructive, pas de sélecteur nécessaire).
 */
export const UpdateMemoryArgsSchema = z
  .object({
    /** Bloc cible de la mutation. */
    target: MemoryWriteTargetSchema,

    /** Nature de l'opération. */
    op: MemoryWriteOpSchema,

    /**
     * Contenu texte de l'opération.
     * Requis pour `replace`, `append`, `delete`. Absent/vide pour `clear`.
     */
    content: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const { op, content } = data;
    const trimmed = content?.trim() ?? '';

    if (op === 'replace' || op === 'append' || op === 'delete') {
      // Ces ops nécessitent un contenu non-vide comme payload ou sélecteur.
      if (trimmed.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `update_memory: op '${op}' requires non-empty content`,
          path: ['content'],
        });
      }
    }

    if (op === 'clear') {
      // clear vide le bloc en entier — un content non-vide serait ambigu et erroné.
      if (trimmed.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `update_memory: op 'clear' must not carry content (it empties the block)`,
          path: ['content'],
        });
      }
    }
  });

/**
 * Type TypeScript inféré de UpdateMemoryArgsSchema.
 */
export type UpdateMemoryArgs = z.infer<typeof UpdateMemoryArgsSchema>;

/**
 * Résultat structuré retourné par les executors des tools mémoire au LLM.
 *
 * Convention : un refus ou une erreur executor = `{ ok: false, message }` structuré,
 * jamais un throw (le LLM verrait un tool_call_error brut non interprétable).
 */
export const MemoryToolResultSchema = z.object({
  /** `true` si l'opération a réussi, `false` sinon. */
  ok: z.boolean(),

  /** Bloc concerné (optionnel — absent si l'erreur est antérieure au dispatch). */
  label: MemoryWriteTargetSchema.optional(),

  /** Message lisible par le LLM — obligatoire en cas d'échec, optionnel en succès. */
  message: z.string().optional(),

  /**
   * Cause d'un refus que l'appelant doit distinguer sans lire le message :
   * `over_cap` (plafond dépassé) et `stale_memory` (le bloc a changé depuis la lecture
   * dont l'écriture dépendait). Absent pour les autres refus et en succès.
   */
  reason: z.enum(['over_cap', 'stale_memory']).optional(),

  /**
   * Taille actuelle de `case_memory`, en caractères : après une écriture réussie, ou
   * inchangée quand l'écriture est refusée au plafond (`reason: 'over_cap'`).
   */
  char_count: z.number().int().nonnegative().optional(),

  /** Plafond du bloc, renvoyé avec `char_count`. */
  char_limit: z.number().int().positive().optional(),
});

/**
 * Type TypeScript inféré de MemoryToolResultSchema.
 */
export type MemoryToolResult = z.infer<typeof MemoryToolResultSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Slice 2 — Audit événements mémoire : MemoryEvent + MemoryEventsResponse
// ────────────────────────────────────────────────────────────────────────────

/**
 * Un événement d'audit mémoire tel qu'exposé via GET /memory/events.
 *
 * On N'expose PAS old_value/new_value : trop volumineux et fuite de contenu
 * inutile pour une UI d'audit sobre.
 */
export const MemoryEventSchema = z.object({
  /** UUID de la ligne user_memory_events */
  id: z.string(),

  /** Label du bloc concerné ('user_profile' | 'user_memory') */
  block_label: z.string(),

  /** Nature de l'opération */
  action: z.enum(['replace', 'append', 'delete', 'clear']),

  /** Origine de la mutation */
  source: z.enum(MEMORY_BLOCK_SOURCES),

  /** Timestamp ISO 8601 de l'événement */
  created_at: z.string(),
});

/**
 * Type TypeScript inféré de MemoryEventSchema.
 */
export type MemoryEvent = z.infer<typeof MemoryEventSchema>;

/**
 * Réponse de `GET /api/cabinet-members/memory/events`.
 */
export const MemoryEventsResponseSchema = z.object({
  events: z.array(MemoryEventSchema),
});

/**
 * Type TypeScript inféré de MemoryEventsResponseSchema.
 */
export type MemoryEventsResponse = z.infer<typeof MemoryEventsResponseSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Slice 3 — Mémoire-dossier (case_memory) : digest + refs + nav tool args
// ────────────────────────────────────────────────────────────────────────────

/**
 * Schéma du digest de mémoire-dossier persisté en DB (`case_memory` table).
 *
 * Le digest encapsule l'avancement et les décisions sur un dossier donné.
 * JAMAIS les faits/parties — ceux-ci vivent dans le Case Wiki.
 * Borné à `CASE_DIGEST_CHAR_LIMIT` caractères à l'écriture (validé par le repo, pas ici).
 *
 * Maille : `case_id + cabinet_id` (multi-tenant, un digest par dossier par cabinet).
 */
export const CaseDigestSchema = z.object({
  /** UUID du dossier concerné */
  case_id: z.string(),

  /** UUID du cabinet (multi-tenant) */
  cabinet_id: z.string(),

  /** Contenu textuel du digest, borné à CASE_DIGEST_CHAR_LIMIT chars */
  digest: z.string(),

  /** Version incrémentée à chaque mutation */
  version: z.number().int(),

  /** Plafond appliqué au digest (copie de CASE_DIGEST_CHAR_LIMIT pour introspection) */
  char_limit: z.number().int(),

  /** Origine de la dernière mise à jour */
  updated_by: z.enum(['agent', 'user', 'seed', 'dream']),

  /** Timestamp ISO 8601 de la dernière mutation */
  updated_at: z.string(),
});

/**
 * Type TypeScript inféré de CaseDigestSchema.
 */
export type CaseDigest = z.infer<typeof CaseDigestSchema>;

/**
 * Schéma d'une entrée du journal `case_memory_refs`.
 *
 * Chaque entrée référence un artefact lié au dossier (conversation, acte généré, etc.)
 * et capture un résumé + les décisions clés prises dans ce contexte.
 * Le journal n'est pas plafonné (contrairement au digest).
 *
 * `kind` est laissé en `z.string()` souple (même logique que `block_label`) —
 * valeurs attendues : `'conversation'`, `'act'`, etc., sans CHECK rigide en Zod
 * pour permettre l'évolution sans migration de schéma.
 */
export const CaseMemoryRefSchema = z.object({
  /** UUID de l'entrée */
  id: z.string(),

  /** UUID du dossier concerné */
  case_id: z.string(),

  /** UUID du cabinet (multi-tenant) */
  cabinet_id: z.string(),

  /**
   * Type d'artefact référencé.
   * Valeurs attendues : 'conversation' | 'act' | … (non-contraint pour flexibilité).
   */
  kind: z.string(),

  /** UUID de l'artefact référencé (conversation_id, act_id, etc.) */
  ref_id: z.string(),

  /** Résumé de ce qui s'est passé dans cet artefact */
  summary: z.string(),

  /** Décisions clés extraites de cet artefact */
  decisions: z.array(z.string()),

  /** Timestamp ISO 8601 de création de l'entrée */
  created_at: z.string(),

  /** Timestamp ISO 8601 de la dernière mise à jour de l'entrée */
  updated_at: z.string(),
});

/**
 * Type TypeScript inféré de CaseMemoryRefSchema.
 */
export type CaseMemoryRef = z.infer<typeof CaseMemoryRefSchema>;

/**
 * Arguments du tool agent `list_case_conversations`.
 *
 * Liste les conversations récentes de l'avocat (nav tool), de la plus récente à la plus ancienne.
 * `case_id` fourni = un dossier de la conversation ; absent = son dossier principal ; une
 * conversation sans aucun dossier ne voit que les conversations de l'avocat sans dossier principal.
 * `limit` absent = défaut appliqué par l'executor (50). `cursor` porte la clé
 * composite du tri pin-first et constitue l'unique mécanisme de continuation.
 * Le schéma est strict : une clé inconnue ne doit jamais devenir une option implicite de
 * découverte des archives lorsque Zod valide les arguments reçus du LLM.
 */
export const ListCaseConversationsArgsSchema = z.object({
  /**
   * Dossier visé (UUID), parmi les dossiers de la conversation (garde de portée de l'agent).
   * Absent = le dossier principal.
   */
  case_id: z.string().uuid().optional(),

  /** Nombre maximum de conversations à retourner (max 200). Absent = défaut executor (50). */
  limit: z.number().int().positive().max(200).optional(),

  /** Curseur opaque renvoyé par la page précédente. */
  cursor: z.string().min(1, 'cursor ne peut pas être vide').optional(),
}).strict();

/**
 * Type TypeScript inféré de ListCaseConversationsArgsSchema.
 */
export type ListCaseConversationsArgs = z.infer<typeof ListCaseConversationsArgsSchema>;

/**
 * Arguments du tool agent `get_conversation`.
 *
 * Récupère le contenu d'une conversation spécifique (nav tool).
 */
export const GetConversationArgsSchema = z
  .object({
    /** UUID de la conversation à récupérer (non vide) */
    conversation_id: z.string().min(1),
    /** Rang (à partir de 1) du premier message de la page ; absent : la dernière page. */
    offset: z.number().int().min(1).optional(),
    /** Caractère du message `offset` où la page commence (message trop long pour une page). */
    char_offset: z.number().int().min(0).optional(),
    /** Messages au plus dans la page. */
    limit: z.number().int().min(1).max(40).optional(),
  })
  .refine((args) => args.char_offset === undefined || args.offset !== undefined, {
    message: 'char_offset désigne un caractère du message offset : il se donne avec offset (recopier previous ou next tels quels).',
    path: ['char_offset'],
  });

/**
 * Type TypeScript inféré de GetConversationArgsSchema.
 */
export type GetConversationArgs = z.infer<typeof GetConversationArgsSchema>;

/**
 * Réponse de l'endpoint UI `GET /api/cases/:caseId/memory` (T16).
 *
 * `digest` est nullable : un dossier peut n'avoir aucune mémoire encore persistée.
 * `refs` est toujours un tableau (vide si aucune entrée).
 */
export const CaseMemoryResponseSchema = z.object({
  /** Digest courant du dossier, null si aucun encore créé */
  digest: CaseDigestSchema.nullable(),

  /** Journal des refs mémoire-dossier */
  refs: z.array(CaseMemoryRefSchema),
});

/**
 * Type TypeScript inféré de CaseMemoryResponseSchema.
 */
export type CaseMemoryResponse = z.infer<typeof CaseMemoryResponseSchema>;
